import classnames from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { generatePath, matchPath, useLocation } from 'react-router';

import { EXTENSIONS_ENUM } from 'constants/ExtensionConstants';
import { PATH, ROUTES } from 'constants/RoutesConstants';
import { getCurrentUserExtensions, getCurrentUserFeatures } from 'modules/users/selectors/currentUser';
import { getCurrentRoute } from 'services/RouterUtils';
import EllipsisTypography from 'ui/base/EllipsisTypography';
import * as styles from 'ui/layout/MainLayout.module.less';
import settings from 'ui/layout/NavigationBar/config';

import { useRights } from '../../../../hooks';

const useNavigationBar = () => {
  const intl = useIntl();
  const { hasAccess } = useRights();
  const location = useLocation();
  const userExtensions = useSelector(getCurrentUserExtensions);
  const userFeatures = useSelector(getCurrentUserFeatures);

  const menuItems = React.useMemo(() => {
    const filterByAccessRights = children =>
      children?.filter(({ extension, to }) => {
        // The isValidExtension test is mandatory since `extension sometimes contains features instead.
        // This can't be changed at the moment because notifications settings rely on the `feature` prop.
        const extensions = Array.isArray(extension) ? extension : [extension];
        const isValidExtension = extensions.every(value => Object.values(EXTENSIONS_ENUM).includes(value));
        const hasAccessToExtension = extensions.some(value => userExtensions.includes(value));

        if (extension && userExtensions && isValidExtension && !hasAccessToExtension) {
          return false;
        }

        if (to) {
          const route = getCurrentRoute(to.path);
          return hasAccess(route);
        }

        return true;
      });

    const getMenu = items =>
      filterByAccessRights(items)?.reduce((acc, { id, name, classItemMenu, iconOverride: IconOverride, icon, children, ...item }) => {
        const label = intl.formatMessage({ id: name });
        const entry = {
          ...item,
          key: id,
          label: <EllipsisTypography clamp={2}>{label}</EllipsisTypography>,
          title: label,
          icon: IconOverride ? <IconOverride /> : <i className={classnames(['fb-icon', icon])} />,
          children: getMenu(children),
          className: classItemMenu,
          popupClassName: styles['sidebar-menu'],
          popupOffset: [0, 0],
        };
        if (entry.children?.length > 0 || entry.to) {
          acc.push(entry);
        }
        return acc;
      }, []);

    return getMenu(settings);
  }, [intl, userExtensions, userFeatures, hasAccess]);

  const getKeys = React.useCallback(() => {
    const findItem = (siblings, target) => {
      // First, find among our siblings
      const item = siblings.find(({ to, params }) => {
        if (to) {
          if (params) {
            return Boolean(matchPath(location.pathname, generatePath(to.path, params)));
          }
          // Test child path
          return Boolean(matchPath({ path: target.path.replace(/:.*/, '') }, to.path.replace(/:.*/, '')));
        }
        return false;
      });

      if (!item) {
        // Then, find among our siblings' children
        return siblings.reduce((acc, sibling) => {
          if (!acc && sibling?.children) {
            // If we found the item among their descendants, we return it (make it go up the tree until
            // we reach the root item). If we didn't find it, we return null.
            const descendant = findItem(sibling.children, target);
            return descendant?.item
              ? {
                // The parent is our sibling, and our descendant's parents (if any)
                parents: [...(descendant.parents ?? []), sibling],
                // The One
                item: descendant.item,
              }
              : acc;
          }

          return acc;
        }, null);
      }
      // If we found the item among our direct children, we return it
      // There are no parents, because we don't know them here
      return { item };
    };

    let currentRoute = getCurrentRoute(location.pathname);
    if (currentRoute === ROUTES.STATS_AUDITS) {
      currentRoute = 'STATS_ODITOO';
    }

    const currentPath = PATH[currentRoute];

    if (currentPath) {
      const { parents, item } = findItem(menuItems, currentPath) ?? {};

      return {
        selectedKeys: item ? [item.key] : [],
        openedKeys: parents?.map(({ key }) => key) ?? [],
      };
    }

    return {
      selectedKeys: [],
      openedKeys: [],
    };
  }, [menuItems, location]);

  const dashboardTitle = React.useMemo(() => {
    const {
      selectedKeys: [selectedKey],
      openedKeys: [openedKey],
    } = getKeys();

    const parentKey = openedKey ?? selectedKey;
    const childKey = selectedKey;

    const parent = settings.find(({ id }) => id === parentKey);
    const child = parent?.children?.find(({ id }) => id === childKey);

    if (parent) {
      const parentTitle = intl.formatMessage({ id: parent.name });
      const childTitle = child ? intl.formatMessage({ id: child.name }) : undefined;

      return [parentTitle, childTitle].filter(Boolean).join(' - ');
    }

    return undefined;
  }, [getKeys, intl]);

  return React.useMemo(() => ({ menuItems, getKeys, dashboardTitle }), [menuItems, getKeys, dashboardTitle]);
};

export default useNavigationBar;
